import type {
  ConditionApplyToOption,
  ConditionSection,
  ConditionSelection,
  ConditionSet,
} from '../conditions.types';
import {
  CONDITION_SELECTION_TYPES,
  isMultiselectRangeValue,
  isRangeValue,
} from './conditionSelection.util';
import { validateRange } from './validateRange';

/**
 * A section's own bounds are a suggestion while a group is being edited — the
 * modal renders the message and still lets the selection be saved. Submitting
 * is where they bind, so the same check runs again over the whole editor.
 */
const sectionError = (
  section: ConditionSection,
  selection: ConditionSelection
): string | undefined => {
  if (section.type === 'multiselect') {
    return undefined;
  }

  return CONDITION_SELECTION_TYPES.reduce<string | undefined>(
    (found, selectionType) => {
      if (found) {
        return found;
      }

      const value = selection[selectionType][section.key];

      if (section.type === 'range') {
        /** A section holds one range, so a multi-item descriptor bounds its first */
        const [item] = section.items;

        return item && isRangeValue(value)
          ? validateRange(item, value, section)
          : undefined;
      }

      return isMultiselectRangeValue(value)
        ? Object.values(value).reduce<string | undefined>(
            (message, range) =>
              message ?? validateRange(section.range ?? {}, range, section),
            undefined
          )
        : undefined;
    },
    undefined
  );
};

/**
 * The first bounds error anywhere in an editor, or undefined when every
 * range it holds is valid. Returning a message rather than a list keeps it
 * usable as a react-hook-form `validate` rule, which blocks submitting while
 * the group itself keeps rendering the same message beside the offending row.
 */
export const validateConditionSets = (
  conditionSets: ConditionSet[],
  applyToOptions: ConditionApplyToOption[]
): string | undefined => {
  for (const { applyTo, selections } of conditionSets) {
    const sections =
      applyToOptions.find((option) => option.value === applyTo)?.conditions ??
      [];

    for (const section of sections) {
      // Returns the first discovered range error
      const message = sectionError(section, selections);

      if (message) {
        return message;
      }
    }
  }

  return undefined;
};
